import React, { useState } from 'react';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, SECTION_TITLE } from './adminStyles';
import { EntryNotifyFields } from './EntryNotify';
import { guestEmailProblem } from '../../utils/calendarNotify';
import { NO_NOTIFICATION, createCalendarEntry, deleteCalendarEntry, updateCalendarEntry, type CalendarEntryDraft, type EntryNotify } from '../../utils/calendarEntries';

/**
 * Adding or editing one of Portal OS's own diary entries — a client meeting,
 * a recce, a hold (13 Sep 2026).
 *
 * Deliberately smaller than the call sheet builder. A call sheet is a
 * production record that feeds the client's schedule and the crew link; this
 * is a line in a diary. Asking for a project, a client and a call time to
 * write "Tom meeting with client" would be the wrong trade.
 *
 * Leave the times blank and it goes in as an all-day entry, which is how most
 * of these are actually used.
 */
export interface EntryEdit {
  id?: string;
  draft: CalendarEntryDraft;
}
export function EntryEditor({
  edit,
  onSaved,
  onCancel
}: {
  edit: EntryEdit;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<CalendarEntryDraft>(edit.draft);
  /* Silent by default since 24 Sep 2026 — see EntryNotify.tsx. */
  const [notify, setNotify] = useState<EntryNotify>(NO_NOTIFICATION);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState('');
  const isNew = !edit.id;
  function set(patch: Partial<CalendarEntryDraft>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    if (!draft.title.trim()) {
      setError('Give it a title — it’s what everyone will see in the calendar.');
      return;
    }
    if (draft.endDate && draft.endDate < draft.startDate) {
      setError('The last day can’t be before the first.');
      return;
    }

    /* Checked here as well as in the database. The database is the real guard,
       but its refusal arrives as a raw function error, and a mistyped address
       is a typo rather than a fault — it deserves a sentence, not a stack.
       Only when something is actually being sent: an address left in a field
       whose switch is off is not going anywhere and should not block a save. */
    if (isNew && notify.notify) {
      const guestProblem = guestEmailProblem(notify.guestEmails);
      if (guestProblem) {
        setError(guestProblem);
        return;
      }
    }
    setSaving(true);
    const result = edit.id ? await updateCalendarEntry(edit.id, draft) : await createCalendarEntry(draft, notify);
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'That could not be saved.');
      return;
    }
    onSaved();
  }
  async function remove() {
    if (!edit.id) return;
    setRemoving(true);
    const result = await deleteCalendarEntry(edit.id);
    setRemoving(false);
    if (!result.ok) {
      setError(result.error ?? 'That could not be removed.');
      return;
    }
    onSaved();
  }
  return <form onSubmit={submit} className="mt-10 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-974" data-mp="1nkjvnek66huv">
      <h2 className={SECTION_TITLE} data-id="element-975" data-mp="1sjwe7n40d2rn">{edit.id ? 'Edit Entry' : 'New Entry'}</h2>
      <p className="label-sm mt-2 max-w-xl leading-relaxed text-paper/35" data-id="element-976" data-mp="1shscnbtl0fuj">
        Goes on the Portal Productions calendar so anyone checking Google sees it. Leave the times
        blank for an all-day entry.
      </p>

      <div className="mt-7 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-977" data-mp="14utzan0rdo0u">
        <label className="block sm:col-span-2" data-id="element-978" data-mp="14sh9jnore9d8">
          <span className="label block text-paper/40" data-id="element-979" data-mp="19lk2pupmjgsw">What is it?</span>
          <input value={draft.title} onChange={event => set({
          title: event.target.value
        })} placeholder="e.g. Tom meeting with client" autoComplete="off" className={FIELD} data-id="element-980" data-mp="c5wlox7hsfef" />
        </label>

        <label className="block" data-id="element-981" data-mp="2iuf07yhl06ci">
          <span className="label block text-paper/40" data-id="element-982" data-mp="2esjwknc6hgvk">Date</span>
          <input type="date" value={draft.startDate} onChange={event => set({
          startDate: event.target.value
        })} className={`${FIELD} [color-scheme:dark]`} data-id="element-983" data-mp="2euekmaiqxp3v" />
        </label>

        <label className="block" data-id="element-984" data-mp="32i4mxranvgev">
          <span className="label block text-paper/40" data-id="element-985" data-mp="32h39jg66ahtz">Last day (if it runs on)</span>
          <input type="date" value={draft.endDate} onChange={event => set({
          endDate: event.target.value
        })} className={`${FIELD} [color-scheme:dark]`} data-id="element-986" data-mp="2xjunv5ltfvnz" />
        </label>

        <label className="block" data-id="element-987" data-mp="36k9b4aicbku4">
          <span className="label block text-paper/40" data-id="element-988" data-mp="36hwy8brfw0nx">Start time (optional)</span>
          <input type="time" value={draft.startTime} onChange={event => set({
          startTime: event.target.value
        })} className={`${FIELD} [color-scheme:dark]`} data-id="element-989" data-mp="1h51pri22uou7" />
        </label>

        <label className="block" data-id="element-990" data-mp="3srvkrcwyvojh">
          <span className="label block text-paper/40" data-id="element-991" data-mp="3sr3pk37a7stq">End time (optional)</span>
          <input type="time" value={draft.endTime} onChange={event => set({
          endTime: event.target.value
        })} className={`${FIELD} [color-scheme:dark]`} data-id="element-992" data-mp="3f1jpx68zzbon" />
        </label>

        <label className="block sm:col-span-2" data-id="element-993" data-mp="350zjlp6mfgzk">
          <span className="label block text-paper/40" data-id="element-994" data-mp="3a1dtlcrs9108">Where (optional)</span>
          <input value={draft.location} onChange={event => set({
          location: event.target.value
        })} placeholder="e.g. Capital Club, DIFC" className={FIELD} data-id="element-995" data-mp="39zko40swomtt" />
        </label>

        <label className="block sm:col-span-2" data-id="element-996" data-mp="u9zq5ex0vxrv">
          <span className="label block text-paper/40" data-id="element-997" data-mp="ubasrbeqezhe">Notes (optional)</span>
          <textarea value={draft.notes} onChange={event => set({
          notes: event.target.value
        })} rows={2} placeholder="Anything the calendar entry should carry" className={`${FIELD} resize-none`} data-id="element-998" data-mp="yb31pp81f7na" />
        </label>
      </div>

      {/* New entries only — see EntryNotify.tsx. */}
      {isNew && <EntryNotifyFields value={notify} onChange={patch => setNotify(current => ({
      ...current,
      ...patch
    }))} data-id="element-999" data-mp="1t9jtt3r36mse" />}

      {error && <p role="alert" className="mt-6 label-sm text-paper/70" data-id="element-1000" data-mp="2q1dm496rv7dh">
          {error}
        </p>}

      {confirmingDelete ? <div className="mt-8 border border-paper/25 px-5 py-5" data-id="element-1001" data-mp="2h6pdqa5wh9kg">
          <p className="font-display text-lg leading-snug" data-id="element-1002" data-mp="2h4w7gvz78rcz">Remove “{draft.title || 'this entry'}”?</p>
          {/* Says what actually happens rather than what would be tidier.
              Tested 13 Sep 2026: the hourly task can create and update events
              on the company calendar but is NOT permitted to delete them, so
              a removal here marks the Google event CANCELLED and frees the
              time — and somebody still has to bin it in Google. Promising a
              clean disappearance would leave a meeting on a shared calendar
              that everyone believes is gone. */}
          <p className="label-sm mt-3 leading-relaxed text-paper/40" data-id="element-1003" data-mp="3q7r1a9m73ofd">
            It goes from Portal OS straight away. On the company calendar it will be retitled
            “CANCELLED” and stop blocking time within the hour — but Portal OS can’t delete Google
            events, so someone needs to delete it there to finish the job.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-5" data-id="element-1004" data-mp="8rreasul2d8n">
            <button type="button" onClick={() => void remove()} disabled={removing} className={BUTTON_SOLID} data-id="element-1005" data-mp="dqludn161np2">
              {removing ? 'Removing…' : 'Yes, remove it'}
            </button>
            <button type="button" onClick={() => setConfirmingDelete(false)} className={BUTTON_GHOST} data-id="element-1006" data-mp="moxqg4ksxdow">
              Keep it
            </button>
          </div>
        </div> : <div className="mt-8 flex flex-wrap items-center justify-between gap-6" data-id="element-1007" data-mp="132mhwfkfjie5">
          <div className="flex flex-wrap items-center gap-5" data-id="element-1008" data-mp="1hytvuy58vnkz">
            <button type="submit" disabled={saving} className={BUTTON_SOLID} data-id="element-1009" data-mp="1d0ipwyi2lx81">
              {saving ? 'Saving…' : edit.id ? 'Save Entry' : 'Add Entry'}
            </button>
            <button type="button" onClick={onCancel} className={BUTTON_GHOST} data-id="element-1010" data-mp="p94u2ikpnjib">
              Cancel
            </button>
          </div>
          {edit.id && <button type="button" onClick={() => setConfirmingDelete(true)} className={BUTTON_QUIET} data-id="element-1011" data-mp="z2u3hm8cui0v">
              Remove Entry
            </button>}
        </div>}
    </form>;
}